import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { IIncidentReportFormData } from '../components/modal/IncidentReportModal'
import { formatDate as formatDateMmDdYyyy } from '../../global/utils/date/mm-dd-yyyy'
import { formatTimeHMmA } from '../../global/utils/date/dates'
import formatCurrencyUsd from '../../global/utils/format-currency-usd'

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return ''
  return formatDateMmDdYyyy(new Date(dateStr))
}

function formatTime(dateStr: string | null | undefined): string {
  if (!dateStr) return ''
  return formatTimeHMmA(new Date(dateStr))
}

function formatCurrency(value: number | null | undefined): string {
  if (value == null) return ''
  return formatCurrencyUsd(Number(value))
}

interface ImageWithBase64 {
  objectId: string
  base64: string
  type: string
}

function formatImageType(type: string): string {
  if (!type) return 'Uploaded'
  return type
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// Common styles
const sectionHeaderStyle = `
  background: #000;
  color: #fff;
  font-weight: bold;
  padding: 6px 8px;
  font-size: 12px;
`

const tableStyle = `
  width: 100%; border-collapse: collapse; margin-bottom: 0;
`

const cellStyle = `
  border: 1px solid #000;
  padding: 4px 6px;
  font-size: 11px;
  vertical-align: top;
`

const labelStyle = `
  ${cellStyle}
  font-weight: bold;
  background: #f5f5f5;
`

const valueStyle = `
  ${cellStyle}
`

const textAreaStyle = `
  ${cellStyle}
  white-space: pre-wrap;
`

function buildPdfHtml(reportData: any, formData: IIncidentReportFormData, images: ImageWithBase64[], orderData?: any): string {
  // Merge order data from reportData.order and orderData prop (fallback)
  const reportOrder = reportData.order || {}
  const fallbackOrder = orderData || {}

  const order = {
    ...fallbackOrder,
    ...reportOrder,
    orderVehicles: reportOrder.orderVehicles?.length > 0
      ? reportOrder.orderVehicles
      : fallbackOrder.orderVehicles || [],
    tireRackOrders: reportOrder.tireRackOrders?.length > 0
      ? reportOrder.tireRackOrders
      : fallbackOrder.tireRackOrders || [],
  }

  const orderVehicle = order.orderVehicles?.[0]
  const customerVehicle = orderVehicle?.customerVehicle
  const tiresCombination = orderVehicle?.tiresCombination
  const customer = order.customer || fallbackOrder.customer
  const trOrder = order.tireRackOrders?.[0]
  const timeSlot = order.timeSlot || fallbackOrder.timeSlot
  const accidentData = reportData.accidentData || {}

  // Computed values
  const techName = reportData.technician?.name || ''
  const vanNumber = reportData.van?.vanNumber || reportData.van?.name || ''
  const installationAddress = reportData.installationPoint?.address || order.customer?.fullAddress || ''
  const trOrderNumber = trOrder?.orderNumber || ''
  const workOrderId = order.workOrderId || ''
  const installationDate = formatDate(timeSlot?.fromTime)
  const installationTime = formatTime(timeSlot?.fromTime)
  const customerName = customer ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim() : ''
  const mileageAtInstall = orderVehicle?.mileage?.toLocaleString() || ''
  const mileageAtIncident = formData.customerMileageAtIncident?.toLocaleString() || ''
  const isOnsiteCancellation = order.status === 'cancelled_onsite' ? 'Yes' : 'No'
  const ymm = customerVehicle
    ? `${customerVehicle.year || ''} ${customerVehicle.brand?.name || ''} ${customerVehicle.model || ''}`.trim()
    : ''
  const numberOfTires = tiresCombination
    ? (tiresCombination.frontCount || 0) + (tiresCombination.rearCount || 0)
    : ''
  const dtClaimsNumber = formData.dtClaimsNumber || ''
  const coachName = reportData.coach?.name || ''
  const incidentDate = formatDate(reportData.incidentDate)
  const jobTitle = formData.jobTitle || ''
  const numTechnicians = formData.numberOfTechnicians ?? ''

  return `
    <div style="font-family: Arial, sans-serif; color: #000; max-width: 800px; margin: 0 auto; padding: 10px; font-size: 11px;">
      <h1 style="text-align: center; font-size: 18px; margin-bottom: 16px; font-weight: bold;">Tire Rack Incident Report</h1>

      <!-- Information Collection Section -->
      <table style="${tableStyle}">
        <tr>
          <td colspan="6" style="${sectionHeaderStyle}">Information Collection</td>
        </tr>
        <tr>
          <td style="${labelStyle} width: 16%;">Tech Name:</td>
          <td style="${valueStyle} width: 17%;">${techName}</td>
          <td style="${labelStyle} width: 16%;">Job Title:</td>
          <td style="${valueStyle} width: 17%;">${jobTitle}</td>
          <td style="${labelStyle} width: 16%;">Van #:</td>
          <td style="${valueStyle} width: 18%;">${vanNumber}</td>
        </tr>
        <tr>
          <td style="${labelStyle}">Address/Location of Incident:</td>
          <td style="${valueStyle}">${installationAddress}</td>
          <td style="${labelStyle}">Exact Time of Incident:</td>
          <td style="${valueStyle}">${installationTime}</td>
          <td style="${labelStyle}">Date of Incident:</td>
          <td style="${valueStyle}">${incidentDate}</td>
        </tr>
        <tr>
          <td style="${labelStyle}">TR Order ID:</td>
          <td style="${valueStyle}">${trOrderNumber}</td>
          <td style="${labelStyle}">Work Order ID:</td>
          <td style="${valueStyle}">${workOrderId}</td>
          <td style="${labelStyle}">Installation Date:</td>
          <td style="${valueStyle}">${installationDate}</td>
        </tr>
        <tr>
          <td style="${labelStyle}">Name of Customer:</td>
          <td style="${valueStyle}">${customerName}</td>
          <td style="${labelStyle}">Cst Mileage at Install:</td>
          <td style="${valueStyle}">${mileageAtInstall}</td>
          <td style="${labelStyle}">Cst Mileage at Incident:</td>
          <td style="${valueStyle}">${mileageAtIncident}</td>
        </tr>
        <tr>
          <td style="${labelStyle}">On-Site Cancellation?</td>
          <td style="${valueStyle}">${isOnsiteCancellation}</td>
          <td style="${labelStyle}">Year/Make/Model:</td>
          <td style="${valueStyle}">${ymm}</td>
          <td style="${labelStyle}">#Tires Installed:</td>
          <td style="${valueStyle}">${numberOfTires}</td>
        </tr>
        <tr>
          <td style="${labelStyle}"># of Technicians:</td>
          <td style="${valueStyle}">${numTechnicians}</td>
          <td style="${labelStyle}">DT Claims #:</td>
          <td style="${valueStyle}">${dtClaimsNumber}</td>
          <td style="${labelStyle}">Cost:</td>
          <td style="${valueStyle}">${formatCurrency(formData.cost)}</td>
        </tr>
      </table>

      <!-- Incident Overview & Resolution Section -->
      <table style="${tableStyle}">
        <tr>
          <td style="${sectionHeaderStyle}">Incident Overview & Resolution</td>
        </tr>
        <tr>
          <td style="${textAreaStyle}">${formData.incidentOverviewAndResolution || ''}</td>
        </tr>
      </table>

      <!-- STEP 1: INVESTIGATION Section -->
      <table style="${tableStyle}">
        <tr>
          <td colspan="4" style="${sectionHeaderStyle}">STEP 1: INVESTIGATION</td>
        </tr>
        <tr>
          <td style="${labelStyle} width: 25%;">Incident Type:</td>
          <td style="${valueStyle} width: 25%;">${formData.incidentType?.replace(/_/g, ' ') || ''}</td>
          <td style="${labelStyle} width: 25%;">Severity:</td>
          <td style="${valueStyle} width: 25%;">${formData.severityOfIncident || ''}</td>
        </tr>
        <tr>
          <td style="${labelStyle}">Review Channel:</td>
          <td style="${valueStyle}">${formData.reviewChannel?.replace(/_/g, ' ') || ''}</td>
          <td style="${labelStyle}">Review Score:</td>
          <td style="${valueStyle}">${formData.reviewScore ?? ''}</td>
        </tr>
        <tr>
          <td style="${labelStyle}">Wheel Off:</td>
          <td style="${valueStyle}">${formData.wheelOff ? 'Yes' : 'No'}</td>
          <td style="${labelStyle}">Lost Time Injury:</td>
          <td style="${valueStyle}">${formData.isLostTimeInjury ? 'Yes' : 'No'}</td>
        </tr>
        ${formData.wheelOff && formData.wheelOffReason ? `
        <tr>
          <td style="${labelStyle}">Wheel Off Reason:</td>
          <td style="${valueStyle}" colspan="3">${formData.wheelOffReason}</td>
        </tr>
        ` : ''}
      </table>

      <!-- Investigation Summary -->
      <table style="${tableStyle}">
        <tr>
          <td style="${labelStyle}">INVESTIGATION: Incident Summary</td>
        </tr>
        <tr>
          <td style="${textAreaStyle}">${formData.investigationSummary || ''}</td>
        </tr>
      </table>

      <!-- Statement from Employee -->
      <table style="${tableStyle}">
        <tr>
          <td style="${labelStyle}">STATEMENT FROM EMPLOYEE(S)</td>
        </tr>
        <tr>
          <td style="${textAreaStyle}">${formData.employeeStatement || ''}</td>
        </tr>
      </table>

      ${formData.isAccident ? `
      <!-- Accident Data -->
      <table style="${tableStyle}">
        <tr>
          <td colspan="4" style="${labelStyle}">ACCIDENT DATA</td>
        </tr>
        <tr>
          <td style="${labelStyle} width: 25%;">Driver Name:</td>
          <td style="${valueStyle} width: 25%;">${accidentData.driverName || ''}</td>
          <td style="${labelStyle} width: 25%;">City/State:</td>
          <td style="${valueStyle} width: 25%;">${accidentData.cityState || ''}</td>
        </tr>
        <tr>
          <td style="${labelStyle}">Number of Injuries:</td>
          <td style="${valueStyle}">${accidentData.numberOfInjuries ?? 0}</td>
          <td style="${labelStyle}">Number of Fatalities:</td>
          <td style="${valueStyle}">${accidentData.numberOfFatalities ?? 0}</td>
        </tr>
        <tr>
          <td style="${labelStyle}">Vehicle Towed:</td>
          <td style="${valueStyle}">${accidentData.isVehicleTowed ? 'Yes' : 'No'}</td>
          <td style="${labelStyle}">Hazmat Release:</td>
          <td style="${valueStyle}">${accidentData.isHazmatRelease ? 'Yes' : 'No'}</td>
        </tr>
        <tr>
          <td style="${labelStyle}">Driver Citation:</td>
          <td style="${valueStyle}">${accidentData.isDriverCitation ? 'Yes' : 'No'}</td>
          <td style="${labelStyle}"></td>
          <td style="${valueStyle}"></td>
        </tr>
      </table>
      ` : ''}

      ${images.length > 0 ? `
      <!-- Photos Header -->
      <table style="${tableStyle}">
        <tr>
          <td style="${labelStyle}">Photos (${images.length})</td>
        </tr>
      </table>
      <!-- Individual Photos -->
      ${images.map((image) => `
        <div style="display: inline-block; margin: 4px; text-align: center; vertical-align: top; border: 1px solid #ddd; padding: 4px;">
          <img src="${image.base64}" alt="${formatImageType(image.type)}" width="180" height="180" style="object-fit: contain;" />
          <p style="font-size: 10px; margin-top: 4px; color: #666;">${formatImageType(image.type)}</p>
        </div>
      `).join('')}
      ` : ''}

      <!-- STEP 2: ROOT CAUSE ANALYSIS -->
      <table style="${tableStyle}">
        <tr>
          <td style="${sectionHeaderStyle}">STEP 2: ROOT CAUSE ANALYSIS</td>
        </tr>
        <tr>
          <td style="${labelStyle}">Root Cause Analysis: Identify what factor(s) caused this incident. Utilize the "5 Whys" to dig down to the root cause.</td>
        </tr>
        <tr>
          <td style="${textAreaStyle}">${formData.rootCauseAnalysis || ''}</td>
        </tr>
      </table>

      <!-- STEP 3: RECOMMENDATIONS -->
      <table style="${tableStyle}">
        <tr>
          <td style="${sectionHeaderStyle}">STEP 3: RECOMMENDATIONS</td>
        </tr>
        <tr>
          <td style="${labelStyle}">Provide recommended follow-up actions based on this incident.</td>
        </tr>
        <tr>
          <td style="${textAreaStyle}">${formData.recommendations || ''}</td>
        </tr>
      </table>

      <!-- STEP 4: CORRECTIVE ACTION PLAN -->
      <table style="${tableStyle}">
        <tr>
          <td style="${sectionHeaderStyle}">STEP 4: CORRECTIVE ACTION PLAN & FOLLOW-UP</td>
        </tr>
        <tr>
          <td style="${labelStyle}">Describe the Corrective Action/Preventative Action Plan</td>
        </tr>
        <tr>
          <td style="${textAreaStyle}">${formData.correctionActionPlan || ''}</td>
        </tr>
      </table>

      <!-- Signature Section -->
      <table style="${tableStyle} margin-top: 20px;">
        <tr>
          <td style="${labelStyle} width: 25%;">Employee Name:</td>
          <td style="${valueStyle} width: 25%;">${techName}</td>
          <td style="${labelStyle} width: 25%;">Coach/Instructor Name:</td>
          <td style="${valueStyle} width: 25%;">${coachName}</td>
        </tr>
        <tr>
          <td style="${labelStyle}">Date:</td>
          <td style="${valueStyle}" colspan="3">${incidentDate}</td>
        </tr>
      </table>
    </div>
  `
}

export async function generateIncidentReportPdf(
  reportData: any,
  formData: IIncidentReportFormData,
  images: ImageWithBase64[] = [],
  orderData?: any,
) {
  const html = buildPdfHtml(reportData, formData, images, orderData)
  const container = document.createElement('div')
  container.innerHTML = html
  document.body.appendChild(container)

  // Wait for all images in the container to load
  const imgElements = container.querySelectorAll('img')
  if (imgElements.length > 0) {
    await Promise.all(
      Array.from(imgElements).map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) {
              resolve()
            } else {
              img.onload = () => resolve()
              img.onerror = () => resolve()
            }
          }),
      ),
    )
  }

  const workOrderId = formData.workOrderId || 'Unknown'
  const dateStr = new Date().toISOString().split('T')[0]
  const filename = `Tire_Rack_Incident_Report_WO${workOrderId}_${dateStr}.pdf`

  const canvas = await html2canvas(container, { scale: 2 })
  document.body.removeChild(container)

  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 10
  const contentWidth = pageWidth - margin * 2
  const contentHeight = pageHeight - margin * 2

  const imgData = canvas.toDataURL('image/jpeg', 0.98)
  const imgWidth = contentWidth
  const imgHeight = (canvas.height * contentWidth) / canvas.width

  if (imgHeight <= contentHeight) {
    pdf.addImage(imgData, 'JPEG', margin, margin, imgWidth, imgHeight)
  } else {
    // Multi-page: slice the canvas into page-sized chunks
    const scaleFactor = canvas.width / contentWidth
    const sliceHeight = contentHeight * scaleFactor
    let srcY = 0

    for (let page = 0; srcY < canvas.height; page++) {
      if (page > 0) pdf.addPage()

      const remainingHeight = canvas.height - srcY
      const currentSliceHeight = Math.min(sliceHeight, remainingHeight)

      const pageCanvas = document.createElement('canvas')
      pageCanvas.width = canvas.width
      pageCanvas.height = currentSliceHeight
      const ctx = pageCanvas.getContext('2d')!
      ctx.drawImage(canvas, 0, srcY, canvas.width, currentSliceHeight, 0, 0, canvas.width, currentSliceHeight)

      const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.98)
      const pageImgHeight = (currentSliceHeight / scaleFactor)
      pdf.addImage(pageImgData, 'JPEG', margin, margin, imgWidth, pageImgHeight)

      srcY += sliceHeight
    }
  }

  pdf.save(filename)
}
